import React from "react";
import { WS1_DISCLAIMER_TEXT } from "@/lib/ws1RegCopy";
import { useTranslation, translate } from "@/lib/i18n";

/**
 * WS-1 canonical short-form product disclaimer.
 * Used on analysis, portfolio, locked preview, homepage stock cards,
 * PDF export, and public market snapshot surfaces.
 *
 * The disclaimer copy is localized (EN + DE). The EN string is
 * verbatim from `ws1RegCopy.WS1_DISCLAIMER_TEXT` — canonical
 * regulatory text is NOT rewritten by this change; it is only mirrored
 * into the `research_transparency.disclaimer_*` i18n keys so the
 * component can render in a language forced by a route (e.g.
 * `/de/research-transparenz`) without mutating the persisted
 * app-wide locale.
 *
 * @param {{
 *   compact?: boolean,
 *   tone?: 'muted'|'default',
 *   language?: 'en'|'de'
 * }} props
 */
export function Disclaimer({ compact = false, tone = "muted", language }) {
  // Preference locale from the reactive hook — used when no
  // `language` prop is supplied.
  const { locale } = useTranslation();
  const lang = language === "en" || language === "de" ? language : locale;

  const cls =
    tone === "muted"
      ? "text-xs text-muted-foreground leading-relaxed"
      : "text-sm text-foreground leading-relaxed";

  // Route-forced language wins. When absent we still fall back to
  // the canonical EN constant so any surface that has not yet added
  // i18n coverage keeps its exact regulatory wording.
  const compactText =
    translate("research_transparency.disclaimer_compact", undefined, lang) ||
    "Model-based research, not personalised advice. Investing involves risk.";
  const longText =
    translate("research_transparency.disclaimer_long", undefined, lang) ||
    WS1_DISCLAIMER_TEXT;

  return (
    <div
      role="note"
      aria-label="Regulatory disclaimer"
      className={cls}
      data-testid="ws1-disclaimer"
      data-ws1-disclaimer="canonical"
      data-ws1-disclaimer-language={lang}
      lang={lang}
    >
      {compact ? <span>{compactText}</span> : <span>{longText}</span>}
    </div>
  );
}

export default Disclaimer;
